import { Alert, Box, LinearProgress } from "@mui/material";

import { ProductionTable } from "./table/production/ProductionTable";
import {
  PlanTableDependencies,
  usePlanTableDependencies,
} from "./table/dependencies";
import { LoadedContent } from "components/common/loading/loadedContent";
import { useTenantTranslation } from "hooks/formatters";
import { usePeriodIndex } from "hooks/periodIndex";
import { useTenant } from "hooks/settings";
import { useGetPlanResultsQuery } from "src/store/api/generatedApi";

interface Props {
  planId: number;
}

export const ProductionTab = ({ planId }: Props) => {
  const { t } = useTenantTranslation();
  const dependencies = usePlanTableDependencies(planId);
  const period = usePeriodIndex();
  const tenantName = useTenant();
  const planResults = useGetPlanResultsQuery({ tenantName, planId, period });
  return (
    <Box
      sx={{
        pt: 2,
        minHeight: 0,
      }}
    >
      <LoadedContent<PlanTableDependencies>
        data={dependencies}
        disableFetching
        loading={<LinearProgress />}
        waiting={<LinearProgress />}
      >
        {({
          plan: {
            mix_metadata,
            summary: { mix_summary },
          },
          productGroups,
          chemistryGroups: { byIndex: chemistryGroups },
          materials: { byIndex: materials },
        }) => {
          if (planResults.isError) {
            return <Alert severity="error">{t("errorLoadingPlanData")}</Alert>;
          }
          const currentData = planResults.currentData;
          if (!currentData) {
            return <LinearProgress />;
          }
          const periodResults = currentData.periods[period.toString()];
          if (!periodResults || periodResults.mixes.length === 0) {
            return <Alert severity="info">{t("noMixesFound")}</Alert>;
          }
          return (
            <ProductionTable
              productGroups={productGroups?.byIndex ?? null}
              chemistryGroups={chemistryGroups}
              materials={materials}
              mixResults={periodResults.mixes}
              productGroupResults={periodResults.product_group_results}
              mixSummaries={mix_summary}
              periodMaterialSummaries={
                currentData.summary.period_material_summary
              }
              period={period}
              mixMetadata={mix_metadata}
            />
          );
        }}
      </LoadedContent>
    </Box>
  );
};
